import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CollapsibleCard } from '../_shared/CollapsibleCard';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { MultilineTextSkeleton } from '@/components/ui/content-loading-skeletons';
import { patientQueries } from '@/features/patient-record/queries';

import { ConsentForm } from './ConsentForm';
import { ConsentList } from './ConsentList';
import { SensitiveFlagBlock } from './SensitiveFlagBlock';

interface ConsentGovernanceSectionProps {
  readonly guid: string;
}

/**
 * Patient-record Consents section (PTV2-1209). Renders the live consent list
 * fed by the shipped `/persons/{guid}/consents` endpoint. The proto governance
 * levels block was removed with PTV2-1209 — the Phase 1 PRD replaces the
 * STANDARD/RESTRICTED/BREAK_GLASS ladder with a boolean sensitive flag
 * (FE-S4 will wire the toggle back in here).
 */
export function ConsentGovernanceSection({
  guid,
}: ConsentGovernanceSectionProps): ReactElement {
  const { t } = useTranslation();
  const consentsQuery = useQuery({ ...patientQueries.consents(guid), placeholderData: keepPreviousData });
  const [adding, setAdding] = useState(false);

  const retry = (): void => {
    Promise.resolve(consentsQuery.refetch()).catch(() => undefined);
  };

  const summaryRows = (consentsQuery.data ?? []).map((c) => ({
    label: c.consentType,
    value: c.status,
    filled: true,
  }));

  const addButton =
    !consentsQuery.isPending && !consentsQuery.isError ? (
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          setAdding(true);
        }}
      >
        <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
        {t('patient.consent.addConsent')}
      </Button>
    ) : undefined;

  return (
    <CollapsibleCard
      idPrefix="consent"
      anchorId="consent"
      title={t('patient.consent.heading')}
      {...(consentsQuery.data !== undefined ? { count: consentsQuery.data.length } : {})}
      headerAction={addButton}
      summary={summaryRows}
    >
      <div className="space-y-4">
        {consentsQuery.isPending ? (
          <MultilineTextSkeleton ariaLabel={t('patient.consent.loading')} />
        ) : consentsQuery.isError ? (
          <FetchErrorCallout
            className="max-w-none"
            error={consentsQuery.error}
            heading={t('patient.consent.error.heading')}
            message={t('patient.consent.error.message')}
            codeLabel={t('routes.patient.error.codeLabel')}
            headingAs="p"
            actions={
              <Button size="sm" onClick={retry}>
                {t('routes.patient.error.retry')}
              </Button>
            }
          />
        ) : (
          <>
            <SensitiveFlagBlock guid={guid} />
            {adding && (
              <ConsentForm
                guid={guid}
                onClose={() => {
                  setAdding(false);
                }}
              />
            )}
            <ConsentList guid={guid} consents={consentsQuery.data} />
          </>
        )}
      </div>
    </CollapsibleCard>
  );
}
